// @flow
import * as React from 'react';
import ContentRow from 'esm-web-pro/components/layout/sidePanel/ContentRow';
import RowActions from 'esm-web-pro/components/layout/sidePanel/RowActions';
import points14Messages from 'esm-web-pro/messages/points14Messages';
import { injectIntl, IntlShape } from 'react-intl';
import ConnectIcon from 'esm-web-pro/components/layout/sidePanel/icons/ConnectIcon';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import DisconnectIcon from 'esm-web-pro/components/layout/sidePanel/icons/DisconnectIcon';
import MeatballsIcon from 'esm-web-pro/components/layout/sidePanel/icons/MeatballsIcon';
import { withRouter } from 'react-router';
import { History, Match } from 'react-router-dom';
import { ClickedRowContext } from 'esm-web-pro/containers/pages/network/objects/point12/ClickedRowContext';
import { highlightFiberCircuit } from 'esm-web-pro/containers/pages/network/highlight';
import useHighlightFiber from 'esm-web-pro/containers/pages/network/objects/fiber/useHighlightFiber';
import { EditorContext } from 'esm-web-pro/containers/map/editor/BaseEditor';
import { useLeaflet } from 'react-leaflet';
import { MapContext } from 'esm-web-pro/containers/map/context';
import { isEmpty } from 'lodash';
import ValueText from 'esm-web-pro/components/layout/sidePanel/ValueText';


type Props = {
    port: ?Object,
    pointCoords: [number, number],
    match: Match,
    history: History,
    tooltipKey?: string,
    onPortConnect?: Function,
    onPortDisconnect?: Function,
    tooltipKey?: string,
    padLevel?: 1 | 2 | 3,

    intl: IntlShape,
};

const OpticalSwitchPortRow = (props: Props) => {
    const {
        port, pointCoords, intl: { formatMessage }, match, history, onPortConnect, onPortDisconnect,
        tooltipKey, padLevel,
    } = props;

    const [clickedRowIdentity, setClickedRowIdentity] = React.useContext(ClickedRowContext);
    const [highlightFiberFunction] = useHighlightFiber();
    const editor = React.useContext(EditorContext);
    const leaflet = useLeaflet();
    const rowId = `PORT-${port.id}`;
    const map = React.useContext(MapContext);

    const actions = [];
    if (!port.port && onPortConnect && map.is_draft) {
        actions.push({
            key: 'sfp-connect',
            icon: <ConnectIcon />,
            size: '20px',
            width: '3px',
            callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onPortConnect({ portToPort: port });
            },
            tooltipKey,
            tooltip: formatMessage(actionMessages.connect),
        });
    } else if (onPortDisconnect && map.is_draft) {
        actions.push({
            key: `${port.uuid}-disconnect`,
            icon: <DisconnectIcon />,
            size: '20px',
            width: '3px',
            callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onPortDisconnect({ portFromPort: port });
            },
            tooltipKey,
            tooltip: formatMessage(actionMessages.disconnect),
        });
    }
    actions.push({
        key: `${port.uuid}-more`,
        icon: <MeatballsIcon />,
        size: '20px',
        width: '3px',
        callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            const path = match.url.split('/layer');
            history.push(`${path[0]}/layer/opticalswitchport/${port.identity}`);
        },
        tooltipKey,
        tooltip: formatMessage(actionMessages.more),
    });

    let title = `${formatMessage(points14Messages.opticalport)} ${port.sequence}`;
    if (port.port) {
        title += ` - ${port.port.ref}`;
    }

    const handleClick = () => {
        setClickedRowIdentity(rowId);

        editor.unregisterAllLoadingCallback();
        const allCoordinates = [];
        const [callbacks, circuitPath] = highlightFiberCircuit(
            port.circuit_path.path, port.port.fiber, leaflet.map,
            highlightFiberFunction, pointCoords,
        );
        allCoordinates.push(...circuitPath);
        editor.registerMultipleLoadingCallbacks(callbacks);
        editor.reloadFeatures();

        if (!isEmpty(allCoordinates)) {
            leaflet.map.highlightedGeometry = {
                bounds: allCoordinates, center: pointCoords,
            };
            leaflet.map.flyToBoundsCenter(allCoordinates, pointCoords);
        } else {
            leaflet.map.highlightedGeometry = {
                bounds: null, center: pointCoords,
            };
            leaflet.map.panTo(pointCoords);
        }
    };

    const rowProps = {};
    if (port.port?.fiber) {
        rowProps.onClick = handleClick;
    }

    return (
        <ContentRow
            padLevel={padLevel}
            rowId={rowId}
            clickedRowIdentity={clickedRowIdentity}
            hoverIndicator="light-2"
            actions={(
                <RowActions
                    iconPadding="3px"
                    gapBetweenActions="small"
                    actions={actions}
                />
            )}
            contentMaxWidth="70%"
            {...rowProps}
        >
            <ValueText>
                {title}
            </ValueText>
        </ContentRow>
    );
};

OpticalSwitchPortRow.defaultProps = {
    tooltipKey: undefined,
    onPortConnect: undefined,
    onPortDisconnect: undefined,
    padLevel: null,
};

export default withRouter(injectIntl(OpticalSwitchPortRow));
